import { useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { motion } from "motion/react";
import { MoreHorizontal, Paperclip, FileText, CheckSquare, Save } from "lucide-react";
import { useTheme } from "../../hooks/useTheme";
import { ThemeToggle } from "../../components/ThemeToggle";

export const ProductDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { isDark, toggleTheme } = useTheme();
  
  const isEditMode = id === 'new';
  const initialCategory = location.state?.category || '';

  
  const handleClose = () => {
    navigate("/admin/products");
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-700 overflow-hidden flex flex-col h-full"
    >
      <div className="flex justify-between items-center p-4 border-b border-slate-100 dark:border-slate-700">
        <h2 className="text-lg font-bold text-slate-800 dark:text-white">
          {isEditMode ? 'ثبت کالای جدید' : 'اطلاعات کالا'}
        </h2>
        <div>
          <ThemeToggle isDark={isDark} toggle={toggleTheme} />
        </div>
      </div>

      <div className="p-6 flex-1 overflow-y-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Column 1 (Right in RTL) -> بارکد */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1 text-left">
                بارکد
              </label>
              <input
                type="text"
                readOnly={!isEditMode}
                defaultValue="000000"
                dir="ltr"
                className={`w-full ${isEditMode ? "bg-white dark:bg-slate-900 focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500" : "bg-slate-100 dark:bg-slate-800/50"} ${isEditMode ? "" : "cursor-not-allowed"} border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm focus:outline-none dark:text-white text-left`}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1 text-left">
                نام رایج
              </label>
              <input
                type="text"
                readOnly={!isEditMode}
                defaultValue="1K,1%"
                className={`w-full ${isEditMode ? "bg-white dark:bg-slate-900 focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500" : "bg-slate-100 dark:bg-slate-800/50"} ${isEditMode ? "" : "cursor-not-allowed"} border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm focus:outline-none dark:text-white`}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1 text-left">
                فروشنده پیشنهادی
              </label>
              <div className="flex gap-2 items-center">
                <button disabled={!isEditMode} className={`bg-slate-200 dark:bg-slate-700/50 ${isEditMode ? "" : "cursor-not-allowed"} text-slate-400 w-8 h-8 rounded shrink-0 flex items-center justify-center`}>
                  <MoreHorizontal size={16} />
                </button>
                <input
                  type="text"
                  readOnly={!isEditMode}
                  defaultValue="Rike"
                  dir="ltr"
                  className={`w-full ${isEditMode ? "bg-white dark:bg-slate-900 focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500" : "bg-slate-100 dark:bg-slate-800/50"} ${isEditMode ? "" : "cursor-not-allowed"} border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm focus:outline-none dark:text-white text-left`}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1 text-left">
                وزن (به گرم)
              </label>
              <input
                type="text"
                readOnly={!isEditMode}
                defaultValue="0.027"
                dir="ltr"
                className={`w-full ${isEditMode ? "bg-white dark:bg-slate-900 focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500" : "bg-slate-100 dark:bg-slate-800/50"} ${isEditMode ? "" : "cursor-not-allowed"} border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm focus:outline-none dark:text-white text-left`}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1 text-left">
                نحوه محاسبه قیمت فروش
              </label>
              <select disabled={!isEditMode} className={`w-full ${isEditMode ? "bg-white dark:bg-slate-900 focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500" : "bg-slate-100 dark:bg-slate-800/50"} ${isEditMode ? "" : "cursor-not-allowed"} border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm focus:outline-none dark:text-white`}>
                <option value="دستی">دستی</option>
                <option value="خودکار">خودکار</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1 text-left">
                کد تعرفه گمرکی
              </label>
              <input
                type="text"
                readOnly={!isEditMode}
                defaultValue="85331020"
                dir="ltr"
                className={`w-full ${isEditMode ? "bg-white dark:bg-slate-900 focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500" : "bg-slate-100 dark:bg-slate-800/50"} ${isEditMode ? "" : "cursor-not-allowed"} border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm focus:outline-none dark:text-white text-left`}
              />
            </div>

            <div className="flex items-center gap-4 mt-8">
              <div className="flex gap-2">
                <button disabled={!isEditMode} className={`p-2 text-emerald-500/50 border-2 border-emerald-500/30 ${isEditMode ? "" : "cursor-not-allowed"} rounded transition-colors`} title="پیوست">
                  <Paperclip size={24} />
                </button>
                <button disabled={!isEditMode} className={`p-2 text-emerald-500/50 border-2 border-emerald-500/30 ${isEditMode ? "" : "cursor-not-allowed"} rounded transition-colors`} title="اطلاعات">
                  <FileText size={24} />
                </button>
                <button disabled={!isEditMode} className={`p-2 text-emerald-500/50 border-2 border-emerald-500/30 ${isEditMode ? "" : "cursor-not-allowed"} rounded transition-colors`} title="ساختار BOM">
                  <CheckSquare size={24} />
                </button>
              </div>

              <label className={`flex items-center gap-2 ${isEditMode ? "" : "cursor-not-allowed"} ml-auto opacity-70`}>
                <span className="text-sm text-slate-600 dark:text-slate-400">فعال</span>
                <input type="checkbox" disabled={!isEditMode} checked className={`w-4 h-4 rounded text-blue-600 focus:ring-blue-500 ${isEditMode ? "" : "cursor-not-allowed"}`} />
              </label>
            </div>
          </div>

          {/* Column 2 (Middle) -> شناسه کالا */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1 text-left">
                شناسه کالا
              </label>
              <input
                type="text"
                readOnly={!isEditMode}
                defaultValue="PR0001"
                dir="ltr"
                className={`w-full ${isEditMode ? "bg-white dark:bg-slate-900 focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500" : "bg-slate-100 dark:bg-slate-800/50"} ${isEditMode ? "" : "cursor-not-allowed"} border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm focus:outline-none dark:text-white text-left font-medium`}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1 text-left">
                پارت نامبر
              </label>
              <input
                type="text"
                readOnly={!isEditMode}
                defaultValue="0603 F 1K"
                dir="ltr"
                className={`w-full ${isEditMode ? "bg-white dark:bg-slate-900 focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500" : "bg-slate-100 dark:bg-slate-800/50"} ${isEditMode ? "" : "cursor-not-allowed"} border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm focus:outline-none dark:text-white text-left font-medium`}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1 text-left">
                سازنده
              </label>
              <div className="flex gap-2 items-center">
                <button disabled={!isEditMode} className={`bg-slate-200 dark:bg-slate-700/50 ${isEditMode ? "" : "cursor-not-allowed"} text-slate-400 w-8 h-8 rounded shrink-0 flex items-center justify-center`}>
                  <MoreHorizontal size={16} />
                </button>
                <input
                  type="text"
                  readOnly={!isEditMode}
                  defaultValue="YAGEO"
                  dir="ltr"
                  className={`w-full ${isEditMode ? "bg-white dark:bg-slate-900 focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500" : "bg-slate-100 dark:bg-slate-800/50"} ${isEditMode ? "" : "cursor-not-allowed"} border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm focus:outline-none dark:text-white text-left`}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1 text-left">
                بسته بندی
              </label>
              <div className="flex gap-2 items-center">
                <button disabled={!isEditMode} className={`bg-slate-200 dark:bg-slate-700/50 ${isEditMode ? "" : "cursor-not-allowed"} text-slate-400 w-8 h-8 rounded shrink-0 flex items-center justify-center`}>
                  <MoreHorizontal size={16} />
                </button>
                <input
                  type="text"
                  readOnly={!isEditMode}
                  defaultValue="Reel-5K"
                  dir="ltr"
                  className={`w-full ${isEditMode ? "bg-white dark:bg-slate-900 focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500" : "bg-slate-100 dark:bg-slate-800/50"} ${isEditMode ? "" : "cursor-not-allowed"} border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm focus:outline-none dark:text-white text-left`}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1 text-left">
                قیمت فروش
              </label>
              <input
                type="text"
                readOnly={!isEditMode}
                defaultValue="0.004"
                dir="ltr"
                className={`w-full ${isEditMode ? "bg-white dark:bg-slate-900 focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500" : "bg-slate-100 dark:bg-slate-800/50"} ${isEditMode ? "" : "cursor-not-allowed"} border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm focus:outline-none dark:text-white text-left`}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1 text-left">
                حداقل سفارش
              </label>
              <input
                type="text"
                readOnly={!isEditMode}
                defaultValue="5000"
                dir="ltr"
                className={`w-full ${isEditMode ? "bg-white dark:bg-slate-900 focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500" : "bg-slate-100 dark:bg-slate-800/50"} ${isEditMode ? "" : "cursor-not-allowed"} border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm focus:outline-none dark:text-white text-left`}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1 text-left">
                قیمت اول دوره
              </label>
              <input
                type="text"
                readOnly={!isEditMode}
                defaultValue="0"
                dir="ltr"
                className={`w-full ${isEditMode ? "bg-white dark:bg-slate-900 focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500" : "bg-slate-100 dark:bg-slate-800/50"} ${isEditMode ? "" : "cursor-not-allowed"} border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm focus:outline-none dark:text-white text-left`}
              />
            </div>
          </div>

          {/* Column 3 (Left in RTL) -> دسته کالا */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1 text-left">
                دسته کالا
              </label>
              <div className="flex gap-2 items-center">
                <button disabled={!isEditMode} className={`bg-slate-200 dark:bg-slate-700/50 ${isEditMode ? "" : "cursor-not-allowed"} text-slate-400 w-8 h-8 rounded shrink-0 flex items-center justify-center`}>
                  <MoreHorizontal size={16} />
                </button>
                <input
                  type="text"
                  readOnly={!isEditMode}
                  defaultValue={initialCategory || "0603"}
                  dir="ltr"
                  className={`w-full ${isEditMode ? "bg-white dark:bg-slate-900 focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500" : "bg-slate-100 dark:bg-slate-800/50"} ${isEditMode ? "" : "cursor-not-allowed"} border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm focus:outline-none dark:text-white text-left`}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1 text-left">
                نام
              </label>
              <input
                type="text"
                readOnly={!isEditMode}
                defaultValue="0603 F 1K"
                dir="ltr"
                className={`w-full bg-blue-50/50 dark:bg-blue-900/10 ${isEditMode ? "" : "cursor-not-allowed"} border border-blue-200/50 dark:border-blue-800/50 rounded-lg px-3 py-2 text-sm focus:outline-none dark:text-white text-left font-medium`}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1 text-left">
                پکیج
              </label>
              <div className="flex gap-2 items-center">
                <button disabled={!isEditMode} className={`bg-slate-200 dark:bg-slate-700/50 ${isEditMode ? "" : "cursor-not-allowed"} text-slate-400 w-8 h-8 rounded shrink-0 flex items-center justify-center`}>
                  <MoreHorizontal size={16} />
                </button>
                <input
                  type="text"
                  readOnly={!isEditMode}
                  defaultValue="0603"
                  dir="ltr"
                  className={`w-full ${isEditMode ? "bg-white dark:bg-slate-900 focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500" : "bg-slate-100 dark:bg-slate-800/50"} ${isEditMode ? "" : "cursor-not-allowed"} border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm focus:outline-none dark:text-white text-left`}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1 text-left">
                واحد اندازه گیری
              </label>
              <div className="flex gap-2 items-center">
                <button disabled={!isEditMode} className={`bg-slate-200 dark:bg-slate-700/50 ${isEditMode ? "" : "cursor-not-allowed"} text-slate-400 w-8 h-8 rounded shrink-0 flex items-center justify-center`}>
                  <MoreHorizontal size={16} />
                </button>
                <input
                  type="text"
                  readOnly={!isEditMode}
                  defaultValue="Pcs"
                  dir="ltr"
                  className={`w-full ${isEditMode ? "bg-white dark:bg-slate-900 focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500" : "bg-slate-100 dark:bg-slate-800/50"} ${isEditMode ? "" : "cursor-not-allowed"} border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm focus:outline-none dark:text-white text-left`}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1 text-left">
                آخرین قیمت خرید
              </label>
              <input
                type="text"
                readOnly={!isEditMode}
                defaultValue="0.0034"
                dir="ltr"
                className={`w-full ${isEditMode ? "bg-white dark:bg-slate-900 focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500" : "bg-slate-100 dark:bg-slate-800/50"} ${isEditMode ? "" : "cursor-not-allowed"} border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm focus:outline-none dark:text-white text-left`}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1 text-left">
                درصد سود فروش
              </label>
              <input
                type="text"
                readOnly={!isEditMode}
                defaultValue="20"
                dir="ltr"
                className={`w-full ${isEditMode ? "bg-white dark:bg-slate-900 focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500" : "bg-slate-100 dark:bg-slate-800/50"} ${isEditMode ? "" : "cursor-not-allowed"} border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm focus:outline-none dark:text-white text-left`}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1 text-left">
                موجودی اول دوره
              </label>
              <input
                type="text"
                readOnly={!isEditMode}
                defaultValue="0"
                dir="ltr"
                className={`w-full ${isEditMode ? "bg-white dark:bg-slate-900 focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500" : "bg-slate-100 dark:bg-slate-800/50"} ${isEditMode ? "" : "cursor-not-allowed"} border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm focus:outline-none dark:text-white text-left`}
              />
            </div>
          </div>
        </div>

        <div className="mt-6 md:w-2/3">
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1 text-left">
            توضیحات
          </label>
          <textarea
            readOnly={!isEditMode}
            rows={4}
            className={`w-full ${isEditMode ? "bg-white dark:bg-slate-900 focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500" : "bg-slate-100 dark:bg-slate-800/50"} ${isEditMode ? "" : "cursor-not-allowed"} border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm focus:outline-none dark:text-white resize-none`}
          ></textarea>
        </div>
      </div>

      <div className="p-4 border-t border-slate-100 dark:border-slate-700 mt-auto flex justify-end gap-2">
        <button
          onClick={handleClose}
          className="px-6 py-2 border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 font-medium transition-colors cursor-pointer"
        >
          {isEditMode ? 'انصراف' : 'بستن'}
        </button>
        {isEditMode && (
          <button
            onClick={() => navigate('/admin/products')}
            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors flex items-center gap-2"
          >
            <Save size={18} />
            ذخیره کالا
          </button>
        )}
      </div>
    </motion.div>
  );
};
